import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Transition
} from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid'
import { Fragment, useState } from 'react'
import {
    AccountRecord,
    ClassifierDetailView,
    useBulkUpdateModerationBoundaryMutation
} from '../../api/client'
import Button from '../Button'
import ConfirmModal from '../ConfirmModal'
import { createClassifierDetailsMap } from './classifierDetailsMapper'
import { mapTemplateValue } from './moderationBoundaryMapper'
import { Queue } from './ModerationContent'
import getModerationTemplates, { ModerationTemplate } from './ModerationTemplates'
import { toastError, toastSuccess } from '../Notification'

type Props = {
    account: AccountRecord
    onTemplateApplied: () => void
    selectedTemplate: ModerationTemplate | undefined
    onSetSelectedTemplate: (selectedTemplate: ModerationTemplate) => void
    classifierDetailsList: ClassifierDetailView[]
    /** Classifier ids whose boundaries are owned by classifier deployment. */
    promptManagedClassifierIds: Set<number>
}

export default function ModerationBoundaryTemplateSelection(props: Props) {
    const {
        account,
        onTemplateApplied,
        selectedTemplate,
        onSetSelectedTemplate,
        classifierDetailsList,
        promptManagedClassifierIds
    } = props

    const [showApplyTemplateDialog, setShowApplyTemplateDialog] = useState<boolean>(false)

    const [bulkUpdateModerationBoundaryMutation, { loading: bulkUpdatingModerationBoundary }] =
        useBulkUpdateModerationBoundaryMutation({
            onError: () => {
                toastError('Failed to bulk update moderation boundaries')
            },
            onCompleted: () => {
                toastSuccess('Moderation boundaries updated.')
            }
        })

    const templates = getModerationTemplates(account.socialMediaServiceShortName || '')

    // Prompt-managed classifiers are left out of the map, so `mapTemplateValue`
    // drops any template entry for them: the backend keeps their existing
    // boundaries through a template apply, and a template must not write new ones.
    const classifierDetailsMap: Map<string, ClassifierDetailView> = createClassifierDetailsMap(
        classifierDetailsList.filter(
            (classifier) => !promptManagedClassifierIds.has(classifier.id)
        )
    )

    const handleApplyTemplate = async () => {
        const mappedSevereBoundaries = mapTemplateValue(
            account.id,
            classifierDetailsMap,
            selectedTemplate?.severe.values(),
            Queue.SEVERE
        )

        const mappedSuspectBoundaries = mapTemplateValue(
            account.id,
            classifierDetailsMap,
            selectedTemplate?.suspect.values(),
            Queue.SUSPECT
        )

        const mappedSafeBoundaries = mapTemplateValue(
            account.id,
            classifierDetailsMap,
            selectedTemplate?.safe.values(),
            Queue.SAFE
        )

        await bulkUpdateModerationBoundaryMutation({
            variables: {
                accountId: account.id,
                moderationBoundaries: [
                    ...mappedSevereBoundaries,
                    ...mappedSuspectBoundaries,
                    ...mappedSafeBoundaries
                ]
            }
        })

        setShowApplyTemplateDialog(false)
        onTemplateApplied()
    }

    return (
        <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm text-ink-700">Compare with</div>
            <Listbox value={selectedTemplate} onChange={onSetSelectedTemplate}>
                <div className="relative w-72">
                    <ListboxButton className="relative flex h-9 w-full cursor-default items-center rounded-md border border-ink-200 bg-white pl-3 pr-9 text-left text-sm text-ink-700 shadow-sm hover:border-ink-300 focus:border-arwen-500 focus:outline-none focus:ring-1 focus:ring-arwen-500">
                        <span className="block truncate">
                            {selectedTemplate?.displayName || 'Select...'}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                            />
                        </span>
                    </ListboxButton>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <ListboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <ListboxOption
                                key="none_selected"
                                className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-4 pr-4 ${
                                        active ? 'bg-ink-50 text-ink-900' : 'text-gray-950'
                                    }`
                                }
                                value=""
                            >
                                {({ selected }) => (
                                    <>
                                        <span
                                            className={`block truncate pl-6 ${
                                                selected ? '' : 'font-normal'
                                            }`}
                                        >
                                            None
                                        </span>
                                        {selected ? (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-arwen-600">
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            </ListboxOption>
                            {templates.map((template, index) => (
                                <ListboxOption
                                    key={index}
                                    className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-4 pr-4 ${
                                            active ? 'bg-ink-50 text-ink-900' : 'text-gray-950'
                                        }`
                                    }
                                    value={template}
                                >
                                    {({ selected }) => (
                                        <>
                                            <span
                                                className={`block truncate pl-6 ${
                                                    selected ? '' : 'font-normal'
                                                }`}
                                            >
                                                {template.displayName}
                                            </span>
                                            {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-arwen-600">
                                                    <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                    />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </ListboxOption>
                            ))}
                        </ListboxOptions>
                    </Transition>
                </div>
            </Listbox>
            {/* Nothing to apply until a template is chosen: applying "None" would
                replace every boundary with an empty set. — Claude */}
            <Button
                primary={false}
                text="Apply template"
                disabled={!selectedTemplate?.displayName}
                onClick={() => setShowApplyTemplateDialog(true)}
            />

            <ConfirmModal
                open={showApplyTemplateDialog}
                onClose={() => setShowApplyTemplateDialog(false)}
                onConfirm={handleApplyTemplate}
                title="Apply this template?"
                description={
                    <>
                        <p>
                            Applying this template will change all existing moderation boundaries
                            to those shown.
                        </p>
                        <p>
                            Boundaries managed by a classifier prompt are left untouched — change
                            those by redeploying the classifier.
                        </p>
                        <p>You cannot undo this action.</p>
                    </>
                }
                confirmLabel="Apply template"
                loading={bulkUpdatingModerationBoundary}
            />
        </div>
    )
}
